import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '../lib/cn';
import { controlClasses, useFieldContext } from './field-context';

/**
 * The number field.
 *
 * `<input type="number">` is refused for three separate reasons. It draws the
 * browser's own spinner arrows, which no token can reach. It silently changes
 * the value on the scroll wheel, so a user scrolling past a form edits a salary
 * without touching it. And it reports an empty string for anything it cannot
 * read, so "1,250.50" and "abc" arrive identically as "nothing".
 *
 * So this is a text input wearing `controlClasses` — the same border, radius
 * and focus ring as `TextInput` — with our own stepper buttons, our own
 * forgiving parser, and no wheel handler anywhere. The keyboard still steps
 * (arrows, and ten times the step with Shift or Page Up/Down), because that is
 * the part of the native control worth keeping.
 *
 * ## Money
 *
 * The rest of the codebase holds money as exact integer centavos — `Money`
 * from `@micarahr/shared-types`, never a float. This field cannot take a
 * `Money`: nothing in the design system knows about HR types, and a control
 * that did could not be used for a headcount or a radius. It takes a plain
 * number, and the caller converts at the edge with `fromPesos`/`toPesos`.
 *
 * What this field owes that arrangement is a value that survives the trip. So
 * every step is computed in the *integer* domain: the value and the step are
 * both scaled by 10^precision, added as whole units, and scaled back. With
 * `precision={2}` that grid is exactly the centavo grid `Money` stores, so
 * `fromPesos(value)` is lossless no matter how many times the stepper was
 * pressed. Rounding shifts the decimal point through the string form for the
 * same reason: `1.005 * 100` is `100.49999999999999`, and rounding that loses
 * a centavo that was really there.
 */

/** Digits after the decimal point in a number's ordinary written form. */
function decimalsOf(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const text = String(Math.abs(value));
  const point = text.indexOf('.');
  // Exponent forms (1e-7) are not step sizes anyone types; treat them as whole.
  return point === -1 ? 0 : text.length - point - 1;
}

/**
 * Rounds to a number of decimal places without float drift.
 *
 * Half rounds *away from zero*, matching `moneyFromDecimalString` in
 * `@micarahr/shared-utils` — so -0.005 and 0.005 both move away from zero, and
 * a peso figure rounded here agrees with the same figure rounded there.
 */
export function roundToPrecision(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  if (String(value).includes('e')) return Number(value.toFixed(Math.max(0, decimals)));
  const shifted = Number(`${value}e${decimals}`);
  if (!Number.isFinite(shifted)) return value;
  const rounded = Math.sign(shifted) * Math.round(Math.abs(shifted));
  return Number(`${rounded}e${-decimals}`);
}

function groupDigits(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * `1250.5` → `1,250.50` at precision 2, `1,250.5` at no precision.
 *
 * Thousands separators are added on display but never while the user is
 * typing, because a separator that appears mid-word moves the caret out from
 * under the fingers.
 */
export function formatNumberValue(value: number, precision?: number): string {
  if (!Number.isFinite(value)) return '';
  const fixed = precision === undefined ? String(value) : value.toFixed(precision);
  if (fixed.includes('e')) return fixed;
  const negative = fixed.startsWith('-');
  const [whole = '', fraction] = fixed.replace('-', '').split('.');
  return `${negative ? '-' : ''}${groupDigits(whole)}${fraction ? `.${fraction}` : ''}`;
}

/** Symbols a pasted amount may carry that are not part of the number. */
const CURRENCY_SIGNS = /^[₱$€£¥]/;
const PERCENT_SIGN = /[%‰]$/;
const NUMERIC = /^[+-]?(\d+(\.\d*)?|\.\d+)$/;

/**
 * Reads a hand-typed or pasted number, or returns `null`.
 *
 * Forgiving about how an amount is written — `1,250.50`, `₱1,250.50` and
 * `1 250.5` are one number — and unforgiving about whether it is one. Anything
 * with a letter in it is refused rather than salvaged, because `12abc` read as
 * 12 is a number the user never typed.
 */
export function parseNumberInput(text: string, affixes: readonly string[] = []): number | null {
  let body = text.trim();

  for (const affix of affixes) {
    const trimmed = affix?.trim().toLowerCase();
    if (!trimmed) continue;
    const lower = body.toLowerCase();
    if (lower.startsWith(trimmed)) body = body.slice(trimmed.length).trim();
    else if (lower.endsWith(trimmed)) body = body.slice(0, body.length - trimmed.length).trim();
  }

  body = body.replace(CURRENCY_SIGNS, '').replace(PERCENT_SIGN, '');
  // `\s` already covers U+00A0 and U+202F, which is what a pasted figure carries.
  body = body.replace(/[\s,_]/g, '');

  if (!NUMERIC.test(body)) return null;
  const parsed = Number(body);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface NumberInputProps {
  /** `null` for empty — which is not the same fact as 0. */
  value?: number | null;
  onChange: (value: number | null) => void;
  /** Fired when the field loses focus — `Controller`'s `field.onBlur`. */
  onBlur?: () => void;
  min?: number;
  max?: number;
  /** How far one arrow press or one stepper click moves the value. */
  step?: number;
  /** Decimal places. Set it to 2 for a peso amount. Unset means "as typed". */
  precision?: number;
  /** Leading unit, e.g. `₱`. Stripped when the same string is pasted back in. */
  prefix?: string;
  /** Trailing unit, e.g. `%`, `days`, `m`. */
  suffix?: string;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  /** Emitted on a hidden input so a native form submit carries the value. */
  name?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** How long the pointer must rest on a stepper before it starts repeating. */
const REPEAT_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 60;

/** Shift and Page Up/Down move ten steps, the same multiplier a native field uses. */
const COARSE_MULTIPLIER = 10;

const stepperClasses = cn(
  'flex flex-1 items-center justify-center text-ink-muted transition-colors',
  'hover:bg-neutral-soft hover:text-ink',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
  'disabled:cursor-not-allowed disabled:text-ink-subtle disabled:hover:bg-transparent',
);

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value,
    onChange,
    onBlur,
    min,
    max,
    step = 1,
    precision,
    prefix,
    suffix,
    disabled,
    placeholder,
    id,
    name,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
  },
  ref,
) {
  const field = useFieldContext();
  const generatedId = useId();
  const inputId = field.id || id || generatedId;

  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Non-null only while the user is typing; otherwise the value is displayed. */
  const [draft, setDraft] = useState<string | null>(null);

  const current = value ?? null;

  const clamp = useCallback(
    (candidate: number): number => {
      if (min !== undefined && candidate < min) return min;
      if (max !== undefined && candidate > max) return max;
      return candidate;
    },
    [min, max],
  );

  const commitValue = useCallback(
    (next: number | null): void => {
      if (next === null) {
        onChange(null);
        return;
      }
      const rounded = precision === undefined ? next : roundToPrecision(next, precision);
      // Clamping last, so a bound is honoured exactly rather than rounded past.
      onChange(clamp(rounded));
    },
    [clamp, onChange, precision],
  );

  /**
   * One press of a stepper or an arrow key.
   *
   * The arithmetic happens on integers: both the base and the step are scaled
   * to whole units of the finest decimal place in play, added, and scaled back.
   * `0.1 + 0.2` never becomes `0.30000000000000004` here, however many times
   * the button is held down.
   */
  const applyStep = useCallback(
    (direction: 1 | -1, multiplier: number): void => {
      // An empty field steps off its floor rather than off nothing.
      const base = current ?? min ?? 0;
      const exponent = Math.max(precision ?? 0, decimalsOf(step), decimalsOf(base));
      const scale = 10 ** exponent;
      const scaled = Math.round(base * scale) + direction * multiplier * Math.round(step * scale);
      setDraft(null);
      commitValue(roundToPrecision(scaled / scale, exponent));
    },
    [commitValue, current, min, precision, step],
  );

  /**
   * The repeat timers read the *latest* step function through a ref, so a value
   * that has moved on between ticks is stepped from where it now is rather than
   * from where the pointer went down.
   */
  const stepRef = useRef(applyStep);
  useEffect(() => {
    stepRef.current = applyStep;
  });

  const repeatRef = useRef<{
    delay?: ReturnType<typeof setTimeout>;
    tick?: ReturnType<typeof setInterval>;
  }>({});

  const stopRepeat = useCallback((): void => {
    const timers = repeatRef.current;
    if (timers.delay) clearTimeout(timers.delay);
    if (timers.tick) clearInterval(timers.tick);
    repeatRef.current = {};
  }, []);

  // A pointer released outside the button, or a component unmounted mid-press,
  // must not leave an interval editing a field nobody is touching.
  useEffect(() => stopRepeat, [stopRepeat]);

  const startRepeat =
    (direction: 1 | -1) =>
    (event: ReactPointerEvent<HTMLButtonElement>): void => {
      // Keeping focus on the input means the caret does not jump to a button that
      // is deliberately not in the tab order.
      event.preventDefault();
      inputRef.current?.focus();
      stepRef.current(direction, 1);
      stopRepeat();
      repeatRef.current.delay = setTimeout(() => {
        repeatRef.current.tick = setInterval(
          () => stepRef.current(direction, 1),
          REPEAT_INTERVAL_MS,
        );
      }, REPEAT_DELAY_MS);
    };

  const commitDraft = (): void => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (!text) {
      if (current !== null) onChange(null);
      return;
    }
    const parsed = parseNumberInput(text, [prefix ?? '', suffix ?? '']);
    // A number this field cannot read is refused, and the display falls back to
    // the last value it could — silently keeping half-typed text would let a
    // form submit a figure nobody agreed to.
    if (parsed === null) return;
    commitValue(parsed);
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        applyStep(event.key === 'ArrowUp' ? 1 : -1, event.shiftKey ? COARSE_MULTIPLIER : 1);
        return;
      case 'PageUp':
      case 'PageDown':
        event.preventDefault();
        applyStep(event.key === 'PageUp' ? 1 : -1, COARSE_MULTIPLIER);
        return;
      case 'Enter':
        // Committing must not also submit the form the field sits in.
        event.preventDefault();
        commitDraft();
        return;
      case 'Escape':
        if (draft !== null) {
          event.preventDefault();
          setDraft(null);
        }
        return;
      default:
    }
  };

  const displayValue = draft ?? (current === null ? '' : formatNumberValue(current, precision));
  const spokenValue = current === null ? undefined : formatNumberValue(current, precision);
  const atMin = min !== undefined && current !== null && current <= min;
  const atMax = max !== undefined && current !== null && current >= max;

  return (
    <div className={cn('relative min-w-0', className)}>
      {prefix ? (
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-ink-muted"
        >
          {prefix}
        </span>
      ) : null}

      <input
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        id={inputId}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        disabled={disabled}
        placeholder={placeholder}
        value={displayValue}
        // The spinbutton role is what `type="number"` would have reported, kept
        // so assistive technology announces the bounds and the current value.
        role="spinbutton"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        aria-valuenow={current ?? undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={
          spokenValue === undefined
            ? undefined
            : `${prefix ?? ''}${spokenValue}${suffix ? ` ${suffix}` : ''}`
        }
        onChange={(event) => {
          const text = event.target.value;
          setDraft(text);
          // The value is published as it is typed so a total computed from this
          // field keeps up, but the *display* stays as typed until blur — no
          // separator appears under the caret, and no bound fights a number
          // that is only half entered.
          if (!text.trim()) {
            if (current !== null) onChange(null);
            return;
          }
          const parsed = parseNumberInput(text, [prefix ?? '', suffix ?? '']);
          if (parsed === null) return;
          onChange(precision === undefined ? parsed : roundToPrecision(parsed, precision));
        }}
        onBlur={() => {
          commitDraft();
          onBlur?.();
        }}
        onKeyDown={onKeyDown}
        style={{
          // `ch` is the width of a zero in the field's own font, which is close
          // enough for the one- and two-character units these affixes are.
          paddingLeft: prefix ? `calc(0.75rem + ${prefix.length}ch + 0.25rem)` : undefined,
          paddingRight: suffix ? `calc(2rem + ${suffix.length}ch + 0.25rem)` : '2rem',
        }}
        className={cn(
          controlClasses,
          'h-9 tabular-nums',
          field.invalid
            ? 'border-danger focus:ring-danger/25'
            : 'border-line-strong focus:border-brand',
        )}
      />

      {suffix ? (
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 -translate-y-1/2 text-base text-ink-muted"
          style={{ right: '2.25rem' }}
        >
          {suffix}
        </span>
      ) : null}

      {/* The steppers are not tab stops: a keyboard user steps with the arrow
          keys from the field itself, exactly as a native number input works,
          and two extra stops per field is a long form made longer. They are
          drawn even while the field is disabled, so the text does not reflow
          as a form is unlocked. */}
      <div className="absolute inset-y-px right-px flex w-7 flex-col overflow-hidden rounded-r-md border-l border-line">
        <button
          type="button"
          tabIndex={-1}
          aria-label="Increase"
          disabled={disabled || atMax}
          onPointerDown={startRepeat(1)}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          className={cn(stepperClasses, 'border-b border-line')}
        >
          <ChevronUp className="h-3 w-3" aria-hidden />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label="Decrease"
          disabled={disabled || atMin}
          onPointerDown={startRepeat(-1)}
          onPointerUp={stopRepeat}
          onPointerLeave={stopRepeat}
          onPointerCancel={stopRepeat}
          className={stepperClasses}
        >
          <ChevronDown className="h-3 w-3" aria-hidden />
        </button>
      </div>

      {name ? (
        <input type="hidden" name={name} value={current === null ? '' : String(current)} />
      ) : null}
    </div>
  );
});
